import type { IntlShape } from "@formatjs/intl";
import { DatePicker, Form, Modal } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  assignmentScheduleActions,
  type ScheduleChangeSource,
} from "../../features/assignment-schedule/assignmentScheduleSlice";
import type { TechnicianAssignmentSchedule } from "../generated-axios-client";
import type { RootState } from "../store";

const API_DATE_FORMAT = "YYYY-MM-DD";

type Props = {
  intl: IntlShape;
  booking: TechnicianAssignmentSchedule | null;
  source: ScheduleChangeSource;
  onClose: () => void;
};

export const EditBookingDatesModal = ({ intl, booking, source, onClose }: Props) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm<{ period: [Dayjs, Dayjs] }>();
  const saveStatus = useSelector(
    (state: RootState) => state.assignmentSchedule.saveStatus
  );
  // once a booking has started only its end can move
  const started = booking !== null && booking.status !== "SCHEDULED";

  useEffect(() => {
    if (!booking) return;
    dispatch(assignmentScheduleActions.setSaveStatus("idle"));
    form.setFieldsValue({
      period: [dayjs(booking.start_date), dayjs(booking.end_date)],
    });
  }, [booking, form, dispatch]);

  useEffect(() => {
    if (booking && saveStatus === "succeeded") {
      dispatch(assignmentScheduleActions.setSaveStatus("idle"));
      onClose();
    }
  }, [booking, saveStatus, dispatch, onClose]);

  const handleSave = async () => {
    if (!booking?.id) return;
    const scheduleId = booking.id;
    const { period } = await form.validateFields();
    dispatch(
      assignmentScheduleActions.updateScheduleDates({
        scheduleId,
        startDate: period[0].format(API_DATE_FORMAT),
        endDate: period[1].format(API_DATE_FORMAT),
        source,
      })
    );
  };

  return (
    <Modal
      open={booking !== null}
      title={intl.formatMessage({ id: "schedule_edit_title" })}
      okText={intl.formatMessage({ id: "schedule_modal_save" })}
      cancelText={intl.formatMessage({ id: "cancel" })}
      onOk={handleSave}
      onCancel={onClose}
      okButtonProps={{ loading: saveStatus === "loading" }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="period"
          label={intl.formatMessage({ id: "schedule_modal_period" })}
          extra={
            started ? intl.formatMessage({ id: "schedule_edit_started_hint" }) : undefined
          }
          rules={[{ required: true }]}
        >
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            disabled={[started, false]}
            disabledDate={(date) => date.isBefore(dayjs(), "day")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
